import React, { memo, useEffect, useRef, useState } from 'react';

import { IconChevronDown, IconChevronUp, Popper, Spacer } from '@zillow/constellation';

import { StyledCloseButton, StyledIconContainer, StyledPopover } from './style';
import { useClickAway } from 'react-use';

interface Props {
  title?: React.ReactNode;
  isLayoutHorizontal?: boolean;
  // allows to control the dropdown open state via prop for horizontal layout
  pIsOpen?: boolean;
  onClose?(): void;
  onOpen?(): void;
  children?: React.ReactNode;
}

const DropdownMenu = ({
  onClose,
  onOpen,
  title = null,
  isLayoutHorizontal = false,
  pIsOpen = false,
  children = null,
}: Props) => {
  const clickOutsideWrapperRef = useRef(null);
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);

  const [isOpen, setIsOpen] = useState(pIsOpen);

  useClickAway(clickOutsideWrapperRef, () => closeDropdown());

  // settings based on layout
  const offset = isLayoutHorizontal ? [0, 230] : undefined;
  const placement = isLayoutHorizontal ? 'right' : 'bottom-start';

  useEffect(() => {
    if (isLayoutHorizontal && pIsOpen !== isOpen) {
      setIsOpen(pIsOpen);
    }
  }, [pIsOpen, isLayoutHorizontal]);

  const IconChevron = isOpen ? IconChevronUp : IconChevronDown;

  const closeDropdown = () => {
    closeBtnRef?.current?.click();
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    if (isLayoutHorizontal === false) {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    if (onOpen) {
      onOpen();
    }

    if (isLayoutHorizontal === false) {
      setIsOpen(true);
    }
  };

  /**
   * handleContentClick
   *
   * To prevent dropdown from auto closing use attribute, data-auto-close
   *
   * <MenuItem data-auto-close={false}>content...</MenuItem>
   */
  const handleContentClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const { currentTarget, target } = event;
    let tempTarget = target as HTMLElement;

    let isTargetEqToCurrentTarget = false;
    let autoClose = tempTarget.getAttribute('data-auto-close') || null;

    // try and find auto close value
    while (!isTargetEqToCurrentTarget && autoClose === null) {
      autoClose = tempTarget.getAttribute('data-auto-close');
      isTargetEqToCurrentTarget = tempTarget === currentTarget;

      tempTarget = tempTarget.parentNode as HTMLElement;
    }

    if (autoClose === 'false') {
      return;
    }

    closeDropdown();
  };

  const renderMenu = (props: Props) => {
    return (
      <StyledPopover
        data-test="MenuPopover"
        {...props}
        role="Menu"
        isLayoutHorizontal={isLayoutHorizontal}
        // @ts-ignore
        closeButton={<StyledCloseButton ref={closeBtnRef} />}
        body={
          <div data-test="DropdownMenu-Content" ref={clickOutsideWrapperRef} onClick={handleContentClick}>
            {children}
          </div>
        }
      />
    );
  };

  return (
    <Popper
      // @ts-ignore
      triggered={renderMenu}
      onClose={handleClose}
      onOpen={handleOpen}
      offset={offset}
      placement={placement}
      data-test="DropdownMenu"
      arrowRef={null}
      isOpen={isOpen}
    >
      <StyledIconContainer data-popper-placement="right" role="button">
        {title}
        <Spacer marginRight={0.5} />
        {isLayoutHorizontal === false && <IconChevron className="IconChevron" fontColor="brand" size="xs" />}
      </StyledIconContainer>
    </Popper>
  );
};

export default memo(DropdownMenu);
